import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { notebookPalette } from '@/lib/notebookTheme';

interface DoodleProps {
  className?: string;
  color?: string;
  size?: number;
  animated?: boolean;
  delay?: number;
}

const drawVariants = (delay: number) => ({
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.8, ease: [0.4, 0, 0.2, 1], delay },
      opacity: { duration: 0.1, delay },
    },
  },
});

// Star doodle
export const DoodleStar: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.pencil.medium,
  size = 24,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M12 2l2.5 7.5H22l-6 4.5 2.5 7.5L12 17l-6.5 4.5 2.5-7.5-6-4.5h7.5L12 2z"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Arrow doodle
export const DoodleArrow: React.FC<DoodleProps & { direction?: 'right' | 'left' | 'up' | 'down' }> = ({
  className,
  color = notebookPalette.pencil.medium,
  size = 32,
  animated = true,
  delay = 0,
  direction = 'right',
}) => {
  const rotations = { right: 0, down: 90, left: 180, up: 270 };
  return (
    <svg
      width={size}
      height={size * 0.5}
      viewBox="0 0 32 16"
      fill="none"
      className={cn('inline-block', className)}
      style={{ transform: `rotate(${rotations[direction]}deg)` }}
    >
      <motion.path
        d="M2 8c4-1 8-0.5 12 0s8 1.5 12-1M22 4c2 1.5 4 3 4 4s-2 2.5-4 4"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        variants={animated ? drawVariants(delay) : undefined}
        initial={animated ? 'hidden' : undefined}
        animate={animated ? 'visible' : undefined}
      />
    </svg>
  );
};

// Underline doodle
export const DoodleUnderline: React.FC<DoodleProps & { width?: number }> = ({
  className,
  color = notebookPalette.pencil.dark,
  width = 100,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={width}
    height={8}
    viewBox={`0 0 ${width} 8`}
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d={`M2 4c${width / 4} -2 ${width / 2} 2 ${width - 4} 0`}
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Circle doodle (for highlighting)
export const DoodleCircle: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.red,
  size = 40,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.ellipse
      cx="20"
      cy="20"
      rx="16"
      ry="14"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      fill="none"
      transform="rotate(-5 20 20)"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Checkmark doodle
export const DoodleCheckmark: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.green,
  size = 24,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M4 12c2 3 4 6 6 8 4-8 8-12 10-14"
      stroke={color}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Spiral doodle
export const DoodleSpiral: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.pencil.light,
  size = 32,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M16 16c0-2 1.5-4 4-4s4 2 4 4-2 4-4 4-6-2-6-6 3-8 8-8 10 4 10 10-5 12-12 12"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Flower doodle
export const DoodleFlower: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.pink,
  size = 28,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M14 8c0-3 2-6 2-6s2 3 2 6c3-1.5 7-1 7-1s-2 3-5 4c3 1 5 4 5 4s-4 .5-7-1c0 3-2 6-2 6s-2-3-2-6c-3 1.5-7 1-7 1s2-3 5-4c-3-1-5-4-5-4s4-.5 7 1z"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
    <motion.circle
      cx="14"
      cy="14"
      r="2"
      fill={notebookPalette.coloredPencil.yellow}
      variants={animated ? { hidden: { scale: 0 }, visible: { scale: 1, transition: { delay: delay + 0.5 } } } : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Lightbulb doodle
export const DoodleLightbulb: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.yellow,
  size = 28,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M14 4c-4 0-7 3-7 7 0 3 2 5 3.5 6.5.5.5.5 1.5.5 2.5h6c0-1 0-2 .5-2.5C19 16 21 14 21 11c0-4-3-7-7-7zM11 22h6M12 24h4"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
    {/* Rays */}
    <motion.path
      d="M14 1v1M22 7l-1 .5M6 7l1 .5M25 14h-1M4 14h-1"
      stroke={color}
      strokeWidth={1}
      strokeLinecap="round"
      opacity={0.6}
      variants={animated ? drawVariants(delay + 0.3) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Exclamation mark doodle
export const DoodleExclamation: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.red,
  size = 24,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M12 4v10"
      stroke={color}
      strokeWidth={3}
      strokeLinecap="round"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
    <motion.circle
      cx="12"
      cy="19"
      r="1.5"
      fill={color}
      variants={animated ? { hidden: { scale: 0 }, visible: { scale: 1, transition: { delay: delay + 0.4 } } } : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Question mark doodle
export const DoodleQuestion: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.blue,
  size = 24,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M8 8c0-3 2-4 4-4s4 1 4 4c0 2-2 3-4 4v2"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
    <motion.circle
      cx="12"
      cy="19"
      r="1.5"
      fill={color}
      variants={animated ? { hidden: { scale: 0 }, visible: { scale: 1, transition: { delay: delay + 0.4 } } } : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Heart doodle
export const DoodleHeart: React.FC<DoodleProps> = ({
  className,
  color = notebookPalette.coloredPencil.red,
  size = 24,
  animated = true,
  delay = 0,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn('inline-block', className)}
  >
    <motion.path
      d="M12 20c-6-4-9-8-9-11 0-2.5 2-5 5-5 2 0 3.5 1.5 4 2.5.5-1 2-2.5 4-2.5 3 0 5 2.5 5 5 0 3-3 7-9 11z"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      variants={animated ? drawVariants(delay) : undefined}
      initial={animated ? 'hidden' : undefined}
      animate={animated ? 'visible' : undefined}
    />
  </svg>
);

// Margin annotation component
export const MarginAnnotation: React.FC<{
  children: React.ReactNode;
  className?: string;
  position?: 'left' | 'right';
  arrowDirection?: 'right' | 'left';
  rotation?: number;
}> = ({
  children,
  className,
  position = 'left',
  arrowDirection,
  rotation = 0,
}) => {
  const arrow = arrowDirection || (position === 'left' ? 'right' : 'left');

  return (
    <motion.div
      initial={{ opacity: 0, x: position === 'left' ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        'flex items-center gap-2 font-handwritten text-sm',
        position === 'left' ? 'flex-row' : 'flex-row-reverse',
        className
      )}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <span className="text-gray-600">{children}</span>
      <DoodleArrow direction={arrow} size={24} color={notebookPalette.pencil.light} />
    </motion.div>
  );
};

// Random doodle scatter component
export const DoodleScatter: React.FC<{
  doodles?: Array<'star' | 'spiral' | 'flower' | 'heart' | 'lightbulb'>;
  count?: number;
  className?: string;
}> = ({
  doodles = ['star', 'spiral', 'flower'],
  count = 5,
  className,
}) => {
  const DoodleMap = {
    star: DoodleStar,
    spiral: DoodleSpiral,
    flower: DoodleFlower,
    heart: DoodleHeart,
    lightbulb: DoodleLightbulb,
  };

  const items = Array.from({ length: count }, (_, i) => ({
    type: doodles[i % doodles.length],
    top: `${Math.random() * 80 + 10}%`,
    left: `${Math.random() * 80 + 10}%`,
    rotation: Math.random() * 30 - 15,
    delay: i * 0.2,
    size: Math.random() * 8 + 16,
  }));

  return (
    <div className={cn('absolute inset-0 pointer-events-none overflow-hidden', className)}>
      {items.map((item, i) => {
        const DoodleComponent = DoodleMap[item.type];
        return (
          <div
            key={i}
            className="absolute"
            style={{
              top: item.top,
              left: item.left,
              transform: `rotate(${item.rotation}deg)`,
            }}
          >
            <DoodleComponent size={item.size} delay={item.delay} />
          </div>
        );
      })}
    </div>
  );
};

export default {
  DoodleStar,
  DoodleArrow,
  DoodleUnderline,
  DoodleCircle,
  DoodleCheckmark,
  DoodleSpiral,
  DoodleFlower,
  DoodleLightbulb,
  DoodleExclamation,
  DoodleQuestion,
  DoodleHeart,
  MarginAnnotation,
  DoodleScatter,
};
